import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, User, Mail, Phone, Building, MapPin, Shield, 
  Plus, Minus, RotateCcw, Lock, Unlock, AlertTriangle, CheckCircle,
  Calendar, Clock, Award, History, Leaf, Package, Link2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  ATTIVO: "bg-green-500/20 text-green-400 border-green-500/30",
  SOSPESO: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  BLOCCATO: "bg-red-500/20 text-red-400 border-red-500/30"
};

const transactionTypeLabels = {
  EARN: { label: "Guadagnato", color: "text-green-400" },
  SPEND: { label: "Speso", color: "text-red-400" },
  ADJUSTMENT: { label: "Rettifica", color: "text-blue-400" }
};

export default function AdminUserDetail({ user: adminUser }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [suggestedRicambista, setSuggestedRicambista] = useState(null);
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  
  // Form states
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [pointsDelta, setPointsDelta] = useState("");
  const [pointsNote, setPointsNote] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API}/admin/users/${userId}/detail`, { 
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserData(data.user);
        setTransactions(data.transactions);
        setLedgerBalance(data.ledger_balance);
        setSuggestedRicambista(data.suggested_ricambista || null);
      } else {
        toast.error("Utente non trovato");
        navigate("/admin/users");
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSuggested = async () => {
    if (!suggestedRicambista) return;
    setLinking(true);
    try {
      const response = await authFetch(`${API}/admin/link-officina-ricambista`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          officina_id: userId,
          ricambista_id: suggestedRicambista.user_id
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Officina collegata al ricambista");
        fetchUserDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore nel collegamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLinking(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus || !statusReason) {
      toast.error("Compila tutti i campi");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/users/${userId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason: statusReason })
      });

      if (response.ok) {
        toast.success("Stato utente aggiornato");
        setStatusDialogOpen(false);
        setStatusReason("");
        fetchUserDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nella modifica");
    }
  };

  const handlePointsAdjust = async () => {
    const delta = parseInt(pointsDelta);
    if (isNaN(delta) || delta === 0 || !pointsNote) {
      toast.error("Inserisci punti validi e una nota");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/users/${userId}/points/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ points_delta: delta, note: pointsNote })
      });

      if (response.ok) {
        toast.success("Punti aggiornati");
        setPointsDialogOpen(false);
        setPointsDelta("");
        setPointsNote("");
        fetchUserDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nella modifica");
    }
  };

  const handlePointsReset = async () => {
    if (!resetReason) {
      toast.error("Inserisci una motivazione");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/users/${userId}/points/reset?reason=${encodeURIComponent(resetReason)}`, {
        method: "POST"
      });

      if (response.ok) {
        toast.success("Punti azzerati");
        setResetDialogOpen(false);
        setResetReason("");
        fetchUserDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nell'azzeramento");
    }
  };

  const handleRoleChange = async () => {
    if (!newRole) {
      toast.error("Seleziona un ruolo");
      return;
    }

    try {
      const response = await authFetch(`${API}/admin/users/${userId}/role-update?role=${newRole}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Ruolo aggiornato");
        setRoleDialogOpen(false);
        fetchUserDetail();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nella modifica");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!userData) return null;

  const isAdmin = adminUser?.role?.toUpperCase() === "ADMIN";
  const userStatus = userData.status || "ATTIVO";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/users")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="page-title">{userData.name || userData.username}</h1>
          <div className="text-zinc-500">ID: {userData.user_id}</div>
        </div>
      </div>

      {/* Status Banner */}
      {userStatus !== "ATTIVO" && (
        <div className={`p-4 rounded-lg border ${statusColors[userStatus]} flex items-center gap-3`}>
          {userStatus === "SOSPESO" ? <AlertTriangle className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
          <div>
            <div className="font-medium">Account {userStatus}</div>
            {userData.status_reason && (
              <div className="text-sm opacity-80">Motivo: {userData.status_reason}</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile */}
          <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
            <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Profilo
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Email</div>
                <div className="text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  {userData.email}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Telefono</div>
                <div className="text-white flex items-center gap-2">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {userData.phone || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Azienda</div>
                <div className="text-white flex items-center gap-2">
                  <Building className="w-4 h-4 text-zinc-500" />
                  {userData.company_name || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">P.IVA</div>
                <div className="text-white font-mono">{userData.vat_number || "-"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-zinc-500 uppercase mb-1">Indirizzo</div>
                <div className="text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-zinc-500" />
                  {userData.address ? `${userData.address}, ${userData.zip_code} ${userData.city} (${userData.province})` : "-"}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800 grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Ruolo</div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-white uppercase">{userData.role || "user"}</span>
                  {isAdmin && (
                    <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                          Modifica
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                          <DialogTitle>Modifica Ruolo</DialogTitle>
                        </DialogHeader>
                        <Select value={newRole} onValueChange={setNewRole}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800">
                            <SelectValue placeholder="Seleziona ruolo" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <DialogFooter>
                          <Button onClick={handleRoleChange}>Salva</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Registrato</div>
                <div className="text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-zinc-500" />
                  {userData.created_at ? new Date(userData.created_at).toLocaleDateString("it-IT") : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Ultimo accesso</div>
                <div className="text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-zinc-500" />
                  {userData.last_login_at ? new Date(userData.last_login_at).toLocaleString("it-IT") : "-"}
                </div>
              </div>
            </div>
          </div>

          {/* Ricambista di fiducia (indicato in registrazione) */}
          {userData.ricambista_info && (
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="ricambista-info-card">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Ricambista di Fiducia
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-1">Ragione Sociale</div>
                  <div className="text-white flex items-center gap-2">
                    <Building className="w-4 h-4 text-zinc-500" />
                    {userData.ricambista_info.company_name || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-1">Partita IVA</div>
                  <div className="text-white font-mono">{userData.ricambista_info.vat_number || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-1">Telefono</div>
                  <div className="text-white flex items-center gap-2">
                    <Phone className="w-4 h-4 text-zinc-500" />
                    {userData.ricambista_info.phone || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-1">Email</div>
                  <div className="text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-zinc-500" />
                    {userData.ricambista_info.email || "-"}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-zinc-500 uppercase mb-1">Indirizzo</div>
                  <div className="text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-zinc-500" />
                    {userData.ricambista_info.address || "-"}
                  </div>
                </div>
              </div>

              {suggestedRicambista && (
                <div 
                  className="mt-4 p-4 rounded-lg border border-primary/40 bg-primary/10 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid="suggested-ricambista-banner"
                >
                  <Link2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">
                      Ricambista registrato trovato: {suggestedRicambista.company_name}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      P.IVA {suggestedRicambista.vat_number} coincide con quella indicata dall'officina in fase di registrazione.
                    </div>
                  </div>
                  <Button
                    data-testid="link-suggested-ricambista-btn"
                    size="sm"
                    className="btn-primary"
                    disabled={linking}
                    onClick={handleLinkSuggested}
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    {linking ? "Collegamento..." : "Collega"}
                  </Button>
                </div>
              )}
              {userData.linked_ricambista_id && (
                <div className="mt-4 text-xs text-green-400 flex items-center gap-2" data-testid="ricambista-linked-badge">
                  <CheckCircle className="w-4 h-4" />
                  Officina collegata a un ricambista registrato
                </div>
              )}
            </div>
          )}

          {/* Transactions */}
          <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
            <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Movimenti Punti
            </h2>
            
            {transactions.length === 0 ? (
              <div className="text-center text-zinc-500 py-8">Nessun movimento</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {transactions.map((t, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`font-mono font-bold ${t.points >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {t.points >= 0 ? '+' : ''}{t.points}
                      </div>
                      <div>
                        <div className="text-sm text-white">
                          <span className={transactionTypeLabels[t.type]?.color || 'text-zinc-400'}>
                            {transactionTypeLabels[t.type]?.label || t.type}
                          </span>
                          {t.brand && <span className="text-zinc-500 ml-2">• {t.brand}</span>}
                        </div>
                        {t.note && <div className="text-xs text-zinc-500">{t.note}</div>}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {new Date(t.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Points Card */}
          <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
            <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Saldo Punti
            </h2>
            
            <div className="text-center py-4">
              <div className="text-5xl font-bold text-primary font-mono">
                {ledgerBalance}
              </div>
              <div className="text-sm text-zinc-500 mt-1">punti disponibili</div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <Dialog open={pointsDialogOpen} onOpenChange={setPointsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full" onClick={() => setPointsDelta("100")}>
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle>Modifica Punti</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-zinc-400">Punti (+ aggiungi, - rimuovi)</label>
                      <Input
                        type="number"
                        value={pointsDelta}
                        onChange={(e) => setPointsDelta(e.target.value)}
                        placeholder="es. 100 o -50"
                        className="bg-zinc-950 border-zinc-800 mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Nota/Motivazione *</label>
                      <Textarea
                        value={pointsNote}
                        onChange={(e) => setPointsNote(e.target.value)}
                        placeholder="Motivo della modifica..."
                        className="bg-zinc-950 border-zinc-800 mt-1"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handlePointsAdjust}>Conferma</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => { setPointsDelta("-100"); setPointsDialogOpen(true); }}
              >
                <Minus className="w-4 h-4 mr-2" />
                Rimuovi
              </Button>
            </div>

            {isAdmin && (
              <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" className="w-full mt-2">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Azzera Punti
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-red-400">Conferma Azzeramento</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      Questa azione azzererà tutti i {ledgerBalance} punti dell'utente. L'operazione è irreversibile.
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Motivazione *</label>
                      <Textarea
                        value={resetReason}
                        onChange={(e) => setResetReason(e.target.value)}
                        placeholder="Motivo dell'azzeramento..."
                        className="bg-zinc-950 border-zinc-800 mt-1"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setResetDialogOpen(false)}>Annulla</Button>
                    <Button variant="destructive" onClick={handlePointsReset}>Conferma Azzeramento</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Status Card */}
          <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
            <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Stato Account
            </h2>
            
            <div className={`p-3 rounded-lg border text-center ${statusColors[userStatus]}`}>
              {userStatus === "ATTIVO" && <CheckCircle className="w-6 h-6 mx-auto mb-1" />}
              {userStatus === "SOSPESO" && <AlertTriangle className="w-6 h-6 mx-auto mb-1" />}
              {userStatus === "BLOCCATO" && <Lock className="w-6 h-6 mx-auto mb-1" />}
              <div className="font-medium">{userStatus}</div>
            </div>

            {isAdmin && (
              <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full mt-4" data-testid="change-status-btn">
                    Cambia Stato
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle>Modifica Stato Account</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-zinc-400">Nuovo Stato</label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1">
                          <SelectValue placeholder="Seleziona stato" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="ATTIVO">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400" />
                              Attivo
                            </span>
                          </SelectItem>
                          <SelectItem value="SOSPESO">
                            <span className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-yellow-400" />
                              Sospeso
                            </span>
                          </SelectItem>
                          <SelectItem value="BLOCCATO">
                            <span className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-red-400" />
                              Bloccato
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Motivazione *</label>
                      <Textarea
                        value={statusReason}
                        onChange={(e) => setStatusReason(e.target.value)}
                        placeholder="Motivo del cambio stato..."
                        className="bg-zinc-950 border-zinc-800 mt-1"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setStatusDialogOpen(false)}>Annulla</Button>
                    <Button onClick={handleStatusChange}>Conferma</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* RENTRI Module Toggle Card */}
          {isAdmin && (
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                Modulo RENTRI
              </h2>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-white">
                    {userData.rentri_enabled ? "Attivo" : "Disattivato"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Registro Nazionale Tracciabilità Rifiuti
                  </div>
                </div>
                <button
                  data-testid="rentri-toggle-btn"
                  onClick={async () => {
                    try {
                      const newVal = !userData.rentri_enabled;
                      const response = await authFetch(`${API}/admin/users/${userId}/rentri-toggle?enabled=${newVal}`, {
                        method: "PATCH"
                      });
                      if (response.ok) {
                        toast.success(`Modulo RENTRI ${newVal ? 'attivato' : 'disattivato'}`);
                        fetchUserDetail();
                      } else {
                        const err = await response.json();
                        toast.error(err.detail || "Errore");
                      }
                    } catch (error) {
                      toast.error("Errore nella modifica");
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    userData.rentri_enabled ? 'bg-primary' : 'bg-zinc-700'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    userData.rentri_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
